import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { fmtDate } from '../lib/utils'
import SourceOfferTotals from '../components/SourceOfferTotals'

function money(value) {
  return `GBP ${(Number(value) || 0).toFixed(2)}`
}

function text(value, fallback = '') {
  return String(value || fallback).trim()
}

function evidenceLabel(item = {}) {
  return item.caption || item.file_name || 'Evidence'
}

export default function GeneralExtraServiceLineApprovalPage() {
  const { token } = useParams()
  const [line, setLine] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [note, setNote] = useState('')
  const [submitting, setSubmitting] = useState('')

  useEffect(() => {
    let cancelled = false
    async function loadLine() {
      setLoading(true)
      setError('')
      try {
        const res = await fetch('/.netlify/functions/general-extra-service-line-response?token=' + encodeURIComponent(token))
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || 'Extra service link could not be loaded.')
        if (!cancelled) setLine(data.line)
      } catch (err) {
        if (!cancelled) setError(err.message || 'Extra service link could not be loaded.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    loadLine()
    return () => { cancelled = true }
  }, [token])

  async function respond(decision) {
    setSubmitting(decision)
    setError('')
    setNotice('')
    try {
      const res = await fetch('/.netlify/functions/general-extra-service-line-response?token=' + encodeURIComponent(token), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision, note }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || 'Could not save your response.')
      setLine(data.line)
      setNotice(decision === 'approved'
        ? 'Thanks, your approval has been sent to the workshop.'
        : 'Thanks, your response has been sent to the workshop.')
    } catch (err) {
      setError(err.message || 'Could not save your response.')
    } finally {
      setSubmitting('')
    }
  }

  const alreadyResponded = ['approved', 'declined', 'completed', 'not_completed'].includes(line?.status)
  const workshop = line?.workshop || {}

  return (
    <div style={{ minHeight: '100vh', background: '#0f0f0f', color: '#fff', padding: '32px 16px' }}>
      <div style={{ maxWidth: 760, margin: '0 auto', background: '#1a1a1a', border: '1px solid #303030', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ padding: '22px 26px', borderBottom: '3px solid #e85d04', background: '#111' }}>
          {workshop.logo_url ? (
            <img src={workshop.logo_url} alt={workshop.name} style={{ maxWidth: 220, maxHeight: 64, objectFit: 'contain', background: '#fff' }} />
          ) : (
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>
              {workshop.name || 'Rebound'} <span style={{ color: '#e85d04' }}>Approval</span>
            </div>
          )}
        </div>

        <div style={{ padding: 26 }}>
          {loading && <div style={{ color: '#aaa' }}>Loading extra service...</div>}
          {error && <div style={{ background: '#331b1b', border: '1px solid #c0392b', color: '#ffb6aa', padding: 12, borderRadius: 6, marginBottom: 16 }}>{error}</div>}
          {notice && <div style={{ background: '#173525', border: '1px solid #2d9e6b', color: '#8ff0bd', padding: 12, borderRadius: 6, marginBottom: 16 }}>{notice}</div>}

          {line && (
            <>
              <h1 style={{ margin: '0 0 6px', fontSize: 26 }}>Extra Service Approval</h1>
              <div style={{ color: '#aaa', marginBottom: 22 }}>{workshop.name} - {fmtDate(line.job?.date)}</div>

              <div style={{ display: 'grid', gap: 10, marginBottom: 18 }}>
                <Info label="Customer" value={line.customer?.name} />
                <Info label="Bike / Item" value={line.job?.bike_name || 'Not specified'} />
                <Info label="Current Job Work" value={line.job?.service_summary || 'General Workshop job'} multiline />
                <Info label="Extra Service" value={line.service_name} />
                {line.service_category && <Info label="Category" value={line.service_category} />}
                {line.service_description && <Info label="Service Details" value={line.service_description} multiline />}
                <Info label="Explanation" value={line.customer_explanation} multiline />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center', background: '#111', borderLeft: '3px solid #e85d04', padding: '16px 18px', marginBottom: 22 }}>
                <span style={{ color: '#aaa', fontSize: 13, textTransform: 'uppercase', letterSpacing: '.06em', fontWeight: 700 }}>Extra service amount</span>
                {line.vat_totals ? <SourceOfferTotals totals={line.vat_totals} /> : <strong style={{ fontSize: 24 }}>{money(line.line_amount)}</strong>}
              </div>

              {line.evidence?.length ? (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ color: '#aaa', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>Evidence</div>
                  <div style={{ display: 'grid', gap: 10 }}>
                    {line.evidence.map(item => (
                      <div key={item.id || evidenceLabel(item)} style={{ border: '1px solid #303030', borderRadius: 6, padding: 12, background: '#111' }}>
                        <strong>{evidenceLabel(item)}</strong>
                        {item.file_name && item.caption ? <div style={{ color: '#aaa', fontSize: 12, marginTop: 3 }}>{item.file_name}</div> : null}
                        {item.signed_url ? <a href={item.signed_url} target="_blank" rel="noopener noreferrer" style={{ color: '#e85d04', display: 'inline-block', marginTop: 8, fontWeight: 700 }}>Open evidence</a> : null}
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              {alreadyResponded ? (
                <div style={{ color: line.status === 'approved' || line.status === 'completed' ? '#2d9e6b' : '#ff6b57', fontWeight: 700 }}>
                  Extra service {text(line.status_label, line.status)}
                </div>
              ) : (
                <>
                  <label style={{ display: 'block', color: '#aaa', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 8 }}>Message to workshop</label>
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Optional note, question, or approval condition..."
                    style={{ width: '100%', minHeight: 90, borderRadius: 6, border: '1px solid #303030', background: '#222', color: '#fff', padding: 12, boxSizing: 'border-box', marginBottom: 14 }}
                  />
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    <button onClick={() => respond('approved')} disabled={!!submitting} style={buttonStyle('#2d9e6b')}>
                      {submitting === 'approved' ? 'Sending...' : 'Approve Extra Service'}
                    </button>
                    <button onClick={() => respond('declined')} disabled={!!submitting} style={buttonStyle('#c0392b')}>
                      {submitting === 'declined' ? 'Sending...' : 'Decline'}
                    </button>
                  </div>
                </>
              )}

              <div style={{ marginTop: 26, borderTop: '1px solid #303030', paddingTop: 14, color: '#888', fontSize: 12 }}>
                No payment is taken here. Questions? Reply to the original email or contact {workshop.email || workshop.name}.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function Info({ label, value, multiline = false }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 12, paddingBottom: 10, borderBottom: '1px solid #303030' }}>
      <div style={{ color: '#888', fontSize: 13 }}>{label}</div>
      <div style={{ color: '#fff', whiteSpace: multiline ? 'pre-line' : 'normal' }}>{value}</div>
    </div>
  )
}

function buttonStyle(background) {
  return {
    border: 0,
    background,
    color: '#fff',
    borderRadius: 6,
    padding: '12px 18px',
    fontWeight: 800,
    cursor: 'pointer',
  }
}
